import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { combineLatest } from 'rxjs';
import { ActionsOperators } from '../../data-rules-model';
import { ICustomPositioningConfig } from '../../lib/custom-component-overlay.directive';
import { StaticOptionsProvider } from '../../lib/option-items-providers';
import { PairedAutocompleteComponent } from '../../lib/paired-autocomplete/paired-autocomplete.component';
import { IActionFieldMeta } from '../../meta-data.service';
import { AllowedActions } from '../allowed-actions';
import { BaseActionComponent } from '../base-action.component';

@Component({
  selector: 'app-action-count',
  templateUrl: './count.component.html',
  imports: [
    PairedAutocompleteComponent
  ],
  styleUrls: ['../action.component.scss']
})
export class CountComponent extends BaseActionComponent implements AfterViewInit {
  filteredToFieldMetaData: IActionFieldMeta[];
  toIsLimited = false;
  errorMessage: string;
  countPlaceholder = 'Choose a field...';

  overlayPositioningConfig: ICustomPositioningConfig = {
    originX: 'center',
    originY: 'bottom',
    overlayX: 'center',
    overlayY: 'top',
    offsetY: 8,
    offsetX: -20
  };

  @ViewChild('actionDestinationCount')
  private fieldPickerComponent: PairedAutocompleteComponent;

  ngAfterViewInit() {
    setTimeout(() => {
      combineLatest(this.getMeta(this.sourceDescriptorControl.value), this.getMeta(this.destinationDescriptorControl.value))
        .subscribe(([srcFieldMeta, dstFieldMeta]) => {
          const srcField = srcFieldMeta.find(fm => fm.fieldName === this.sourceControl.value);
          const dstField = dstFieldMeta.find(fm => fm.fieldName === this.destinationControl.value);
          this.sourceControl.setValidators(Validators.required);
          this.destinationControl.setValidators(Validators.required);
          this.sourceControlValueChange(srcField as IActionFieldMeta);
          this.destinationControlValueChange(dstField as IActionFieldMeta);
        });
    }, 0);
  }

  protected initialise(): void {
    combineLatest(this.getMeta(this.sourceDescriptorControl.value), this.getMeta(this.destinationDescriptorControl && this.destinationDescriptorControl.value))
      .subscribe(([filteredSrcMeta, filteredDstMeta]) => {
        const finalMeta = filteredDstMeta
          .filter(fm => this.filterFields(this.sourceTypeControl.value, fm))
          .filter(fm => {
            const srcFieldMeta = filteredSrcMeta.find(sfm => sfm.fieldName === this.sourceControl.value);
            return AllowedActions.actionAllowed(ActionsOperators[this.typeControl.value as ActionsOperators], srcFieldMeta, srcFieldMeta, fm);
          });
        this.applyNewOptionsProvider(finalMeta);
        this.filteredToFieldMetaData = finalMeta;
      });
  }

  protected initialiseControls() {
    this.fieldToChangeDescriptorControlValueChangeHandler(this.fieldToChangeDescriptorControl.value);
  }

  protected sourceControlValueChange(selectedField: IActionFieldMeta): void {
    if (!selectedField)
      return;
    this.sourceTypeControl.setValue(selectedField.metaType);
    this.getMeta(this.destinationDescriptorControl.value)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {
        const finalMeta = filteredMeta
          .filter(fm => this.filterFields(selectedField.metaType, fm))
          .filter(fm => AllowedActions.actionAllowed(ActionsOperators[this.typeControl.value as ActionsOperators], selectedField, selectedField, fm));
        this.filteredToFieldMetaData = finalMeta;
        this.toIsLimited = true;
        this.applyNewOptionsProvider(finalMeta);
        const dstField = finalMeta.find(fm => fm.fieldName === this.destinationControl.value);
        if (!this.filterFields(selectedField.metaType, dstField)) {
          this.destinationControl.setValue(null);
          this.destinationTypeControl.setValue(null);
        }
      });
  }

  protected destinationControlValueChangeHandler(fieldName: string): void {
    this.actionDestinationType = this.destinationTypeControl.value;
    this.getMeta(this.destinationDescriptorControl.value)
      .subscribe((filteredMeta: IActionFieldMeta[]) => {
        const meta = filteredMeta
          .filter(fm => this.filterFields(this.sourceTypeControl.value, fm))
          .find(fm => fm.fieldName === fieldName);
        this.destinationControlValueChange(meta);
      });
  }

  protected destinationControlValueChange(selectedField: IActionFieldMeta): void {
    if (!selectedField)
      return;
    this.destinationTypeControl.setValue(selectedField.metaType);
    this.toIsLimited = false;
    if (!this.filterFields(this.sourceTypeControl.value, selectedField)) {
      this.sourceControl.setValue(null);
      this.sourceTypeControl.setValue(null);
    }
  }

  protected destinationDescriptorControlValueChangeHandler(desc: string | null) {
    combineLatest(this.getMeta(this.sourceDescriptorControl.value), this.getMeta(desc))
      .subscribe(([filteredSrcMeta, filteredDstMeta]) => {
        const finalMeta = filteredDstMeta
          .filter(fm => this.filterFields(this.sourceTypeControl.value, fm))
          .filter(fm => {
            const srcField = filteredSrcMeta.find(sfm => sfm.fieldName === this.sourceControl.value);
            return AllowedActions.actionAllowed(ActionsOperators[this.typeControl.value as ActionsOperators], srcField, srcField, fm);
          });
        const meta = finalMeta.find(fm => fm.fieldName === this.destinationControl.value);
        if (!meta) {
          this.destinationControl.setValue(null);
          this.destinationTypeControl.setValue(null);
        }
        this.applyNewOptionsProvider(finalMeta);
        this.destinationControlValueChange(meta);
      });
  }

  private applyNewOptionsProvider(meta: IActionFieldMeta[]) {
    const options = meta
      .filter(fm => this.filterFields(this.sourceTypeControl.value, fm))
      .map(fm => ({name: fm.fieldName, label: fm.label}));
    if (options.length === 0) {
      this.errorMessage = 'There are no supported fields for the selected action.';
    } else {
      this.errorMessage = '';
    }
    this.countPlaceholder = this.errorMessage;
    const optionsProvider = new StaticOptionsProvider(options);
    this.fieldPickerComponent.setOptionsProvider(optionsProvider, this.destinationControl.value);
  }

  private filterFields(sourceType: string, destinationMeta: IActionFieldMeta): boolean {
    const destinationType = destinationMeta ? destinationMeta.metaType : '';
    return sourceType === 'TO_MANY' && destinationType === 'INTEGER';
  }
}
